import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Download, Loader2, Paperclip, CheckCircle2, XCircle, Ban, AlertTriangle, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDateTime, fmtMoney } from "@/lib/format";
import { InviteStatusBadge, EnquiryOpenBadge, INCOTERM_OPTIONS, downloadFile } from "@/lib/enquiries";
import SellerAuctionPanel from "@/components/SellerAuctionPanel";
import EventCountdown from "@/components/EventCountdown";
import CollapsibleSection from "@/components/CollapsibleSection";
import { TermsView } from "@/components/TermsEditor";

const EMPTY_OFFER = { gst_pct: "", delivery_days: "", tech_specs: "", incoterms: "EXW", payment_terms: "", comment: "", packaging_cost: "", freight_cost: "" };

export default function SupplierEnquiryDetail() {
  const { inviteId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [closed, setClosed] = useState("");
  const [offer, setOffer] = useState(EMPTY_OFFER);
  const [prices, setPrices] = useState({});
  const [uploadCat, setUploadCat] = useState("technical");
  const [saving, setSaving] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const [termsExpanded, setTermsExpanded] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.get(`/seller/enquiries/${inviteId}`)
      .then((r) => {
        setData(r.data);
        setClosed("");
        const inv = r.data.invite;
        if (inv.status === "participated" && inv.response_review_required && inv.response) {
          const rp = inv.response;
          setOffer({ gst_pct: rp.gst_pct, delivery_days: rp.delivery_days, tech_specs: rp.tech_specs,
                     incoterms: rp.incoterms, payment_terms: rp.payment_terms, comment: rp.comment || "",
                     packaging_cost: rp.packaging_cost || "", freight_cost: rp.freight_cost || "" });
          const pm = {};
          (rp.items || []).forEach((i) => { pm[i.material_code] = i.unit_price; });
          setPrices(pm);
        }
      })
      .catch((e) => {
        if (e?.response?.status === 410) setClosed(formatApiError(e));
        else toast.error(formatApiError(e));
      });
  }, [inviteId]);
  useEffect(() => { load(); }, [load]);

  if (closed) {
    return (
      <div data-testid="seller-enquiry-closed" className="mx-auto max-w-lg space-y-4 py-16 text-center">
        <Ban className="mx-auto h-10 w-10 text-[#EF4444]" />
        <h1 className="font-heading text-xl font-semibold">Submission Closed</h1>
        <p className="text-sm text-muted-foreground">{closed}</p>
        <p className="text-xs text-muted-foreground">You can no longer submit or modify your response because the event timer has expired or you were removed from the event.</p>
        <Button variant="outline" data-testid="closed-back-btn" onClick={() => navigate("/seller/enquiries")}><ArrowLeft className="mr-1.5 h-4 w-4" /> Back to Enquiries</Button>
      </div>
    );
  }
  if (!data) return <div className="flex h-64 items-center justify-center" data-testid="seller-enquiry-loading"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const { invite: inv, enquiry: enq, buyer_org_name, attachments, my_attachments, server_time, terms_version, terms_accepted, terms_acceptance, terms_acceptances } = data;
  const hasTerms = !!(enq.buyer_terms_conditions && enq.buyer_terms_conditions.trim());
  const termsUpdated = hasTerms && !terms_accepted && (terms_acceptances || []).some((a) => a.terms_version < (terms_version || 1));
  const needsTermsAcceptance = hasTerms && !terms_accepted;
  const reviewRequired = inv.status === "participated" && inv.response_review_required;
  const canAct = !enq.deadline_passed && inv.status !== "declined" && inv.status !== "removed" && (inv.status !== "participated" || reviewRequired);
  const activeItems = (enq.items || []).filter((i) => i.status !== "removed");
  const newItemCodes = new Set(activeItems.filter((i) => i.created_at && inv.created_at && i.created_at > inv.created_at).map((i) => i.material_code));

  // Client-side preview only — the backend recomputes all monetary values authoritatively.
  const num = (v) => (v === "" || v == null || isNaN(Number(v)) ? 0 : Number(v));
  const materialBase = activeItems.reduce((s, it) => s + num(prices[it.material_code]) * it.quantity, 0);
  const totalBase = materialBase + num(offer.packaging_cost) + num(offer.freight_cost);
  const gstAmount = totalBase * num(offer.gst_pct) / 100;
  const finalValue = totalBase + gstAmount;

  const intent = async (action, reason = "") => {
    try {
      await api.post(`/seller/enquiries/${inviteId}/intent`, { action, reason });
      toast.success(action === "participate" ? "Marked as intending to participate" : "Enquiry declined");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const acceptTerms = async () => {
    if (!termsChecked) return toast.error("Please tick the checkbox to confirm you have read the Terms & Conditions");
    setSaving(true);
    try {
      await api.post(`/seller/enquiries/${inviteId}/accept-terms`);
      toast.success(`Terms & Conditions v${terms_version} accepted`);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const submitOffer = async (e) => {
    e.preventDefault();
    if (needsTermsAcceptance) return toast.error("You must accept the Buyer's Terms & Conditions before submitting your response.");
    setSaving(true);
    try {
      const r = await api.post(`/seller/enquiries/${inviteId}/respond`, {
        ...offer, gst_pct: Number(offer.gst_pct), delivery_days: Number(offer.delivery_days),
        packaging_cost: offer.packaging_cost === "" ? null : Number(offer.packaging_cost),
        freight_cost: offer.freight_cost === "" ? null : Number(offer.freight_cost),
        items: activeItems.map((it) => ({ material_code: it.material_code, unit_price: Number(prices[it.material_code]) })),
      });
      toast.success(`Offer submitted — final value ${fmtMoney(r.data?.financials?.final_value)}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", uploadCat);
    try {
      await api.post(`/seller/enquiries/${inviteId}/attachments`, fd);
      toast.success("Attachment uploaded");
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { e.target.value = ""; }
  };

  return (
    <div data-testid="seller-enquiry-detail-page" className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" size="icon" data-testid="seller-enquiry-back-btn" onClick={() => navigate("/seller/enquiries")}><ArrowLeft className="h-4 w-4" /></Button>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="font-heading text-2xl font-semibold tracking-tight font-mono" data-testid="seller-enquiry-number">{enq.enquiry_number}</h1>
            <EnquiryOpenBadge passed={enq.deadline_passed} />
            <InviteStatusBadge status={inv.status} />
            <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 font-mono text-xs text-slate-600" data-testid="seller-event-version">v{enq.version || 1}</span>
          </div>
          <p className="text-sm text-muted-foreground">From <span className="font-medium text-slate-700">{buyer_org_name}</span></p>
        </div>
        <Button variant="outline" data-testid="seller-download-enquiry-pdf-btn" onClick={() => downloadFile(`/enquiries/${enq.id}/download`, `${enq.enquiry_number}.pdf`)}>
          <Download className="mr-1.5 h-4 w-4" /> Enquiry PDF
        </Button>
      </div>

      {reviewRequired && (
        <div className="flex items-start gap-2 rounded-lg border border-[#FDE68A] bg-[#FEF3C7]/60 px-5 py-3" data-testid="response-review-required-banner">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[#92400E]" />
          <div>
            <p className="text-sm font-semibold text-[#92400E]">RESPONSE REQUIRES REVIEW</p>
            <p className="text-sm text-[#92400E]">{inv.response_review_note || "The Buyer has modified the Event. Please review and resubmit your quotation."}</p>
          </div>
        </div>
      )}

      <CollapsibleSection id={`seller-inquiry-${inviteId}`} title="Inquiry Details" icon={<FileText className="h-4 w-4 text-slate-500" />} defaultOpen testid="seller-section-inquiry">
        <div className="grid grid-cols-2 gap-x-6 gap-y-4 px-5 pt-4 md:grid-cols-4">
          <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Event ID</p><p className="mt-1 font-mono text-sm font-semibold">{enq.enquiry_number}</p></div>
          <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Timer Ends</p><p className="mt-1 text-sm font-semibold" data-testid="seller-enquiry-deadline">{fmtDateTime(enq.deadline)}</p></div>
          <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Incoterms</p><p className="mt-1 font-mono text-sm">{enq.incoterms}</p></div>
          <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Delivery Required</p><p className="mt-1 text-sm">{enq.delivery_days} days</p></div>
          <EventCountdown deadline={enq.deadline} serverTime={server_time} label="Submission closes in" testid="seller-event-countdown" />
          <div className="col-span-2 md:col-span-3"><p className="text-xs uppercase tracking-wide text-muted-foreground">Description</p><p className="mt-1 text-sm">{enq.description}</p></div>
          {(enq.delivery_address?.line1) && (
            <div className="col-span-2" data-testid="seller-delivery-address">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Deliver To</p>
              <p className="mt-1 text-sm">
                {enq.delivery_address.line1}{enq.delivery_address.line2 ? `, ${enq.delivery_address.line2}` : ""}<br />
                {enq.delivery_address.city}, {enq.delivery_address.state}, {enq.delivery_address.country} — {enq.delivery_address.postal_code}
                {enq.delivery_address.contact_name && <span className="block text-xs text-muted-foreground">Contact: {enq.delivery_address.contact_name} {enq.delivery_address.contact_number}</span>}
              </p>
            </div>
          )}
          {enq.buyer_comment && (
            <div className="col-span-2" data-testid="seller-buyer-comment">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Buyer Comment</p>
              <p className="mt-1 text-sm italic">"{enq.buyer_comment}"</p>
            </div>
          )}
        </div>
        <div className="overflow-x-auto px-5 py-4">
          <table className="w-full text-sm" data-testid="seller-enquiry-items-table">
            <thead>
              <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="py-2 pr-4 font-medium">#</th>
                <th className="py-2 pr-4 font-medium">Material Code</th>
                <th className="py-2 pr-4 font-medium">Description</th>
                <th className="py-2 pr-4 font-medium text-right">Qty</th>
                <th className="py-2 pr-4 font-medium">UOM</th>
                <th className="py-2 pr-4 font-medium">Incoterms</th>
                <th className="py-2 font-medium text-right">Delivery Days</th>
              </tr>
            </thead>
            <tbody>
              {activeItems.map((it, i) => (
                <tr key={it.id || i} className="border-b last:border-0" data-testid={`seller-enquiry-item-row-${i}`}>
                  <td className="py-2.5 pr-4 font-mono text-xs text-muted-foreground">{i + 1}</td>
                  <td className="py-2.5 pr-4 font-mono text-xs font-semibold">
                    {it.material_code}
                    {newItemCodes.has(it.material_code) && (
                      <span className="ml-2 rounded bg-[#DBEAFE] px-1.5 py-0.5 text-[10px] font-bold uppercase text-[#1E40AF]" data-testid={`new-item-badge-${i}`}>New line item added</span>
                    )}
                  </td>
                  <td className="py-2.5 pr-4">{it.description}</td>
                  <td className="py-2.5 pr-4 text-right font-mono">{it.quantity}</td>
                  <td className="py-2.5 pr-4 font-mono text-xs">{it.uom}</td>
                  <td className="py-2.5 pr-4 font-mono text-xs">{it.incoterms || enq.incoterms}</td>
                  <td className="py-2.5 text-right font-mono text-xs">{it.delivery_days || enq.delivery_days}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {attachments.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 border-t px-5 py-3">
            <span className="text-xs uppercase tracking-wide text-muted-foreground">Buyer Attachments</span>
            {attachments.map((f) => (
              <button key={f.id} data-testid={`buyer-attachment-${f.id.slice(0, 8)}`} onClick={() => downloadFile(`/enquiry-files/${f.id}/download`, f.filename)}
                className="flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs hover:bg-slate-50">
                <Paperclip className="h-3 w-3" /> {f.filename}
              </button>
            ))}
          </div>
        )}
      </CollapsibleSection>

      {hasTerms && (
        <div className="rounded-lg border bg-card" data-testid="terms-section">
          <h2 className="border-b px-5 py-3 font-heading text-base font-semibold">Buyer Terms & Conditions <span className="ml-1 font-mono text-xs font-normal text-muted-foreground">v{terms_version}</span></h2>
          <div className="px-5 py-4">
            <div className="relative" data-testid="terms-collapse-wrap">
              <div className={termsExpanded ? "" : "max-h-24 overflow-hidden"}>
                <TermsView text={enq.buyer_terms_conditions} testid="seller-terms-text" />
              </div>
              {!termsExpanded && <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-white to-transparent" />}
            </div>
            <button type="button" data-testid="terms-toggle-btn" onClick={() => setTermsExpanded((v) => !v)}
              className="mt-2 text-sm font-medium text-blue-700 hover:underline">
              {termsExpanded ? "View Less" : "View More"}
            </button>
            {termsUpdated && (
              <p className="mt-3 rounded-md border border-[#FDE68A] bg-[#FEF3C7] px-3 py-2 text-sm font-medium text-[#92400E]" data-testid="terms-updated-banner">
                Buyer has updated the Terms & Conditions — please review and accept the updated terms.
              </p>
            )}
            {terms_accepted ? (
              <p className="mt-3 flex items-center gap-2 text-sm font-medium text-[#166534]" data-testid="terms-accepted-chip">
                <CheckCircle2 className="h-4 w-4" /> You accepted Terms & Conditions v{terms_version}
                {terms_acceptance?.accepted_at && <span className="text-xs font-normal text-muted-foreground">on {fmtDateTime(terms_acceptance.accepted_at)}</span>}
              </p>
            ) : (
              <div className="mt-3 space-y-2">
                <label className="flex cursor-pointer items-start gap-2.5 text-sm">
                  <Checkbox data-testid="terms-accept-checkbox" checked={termsChecked} onCheckedChange={(v) => setTermsChecked(!!v)} />
                  <span>I have read and agree to the {termsUpdated ? "updated " : ""}Buyer's Terms &amp; Conditions.</span>
                </label>
                <Button size="sm" data-testid="accept-terms-btn" disabled={saving || !termsChecked} onClick={acceptTerms} className="bg-slate-900 hover:bg-slate-800">
                  {saving && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />} Accept &amp; Continue
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {inv.status === "declined" && (
        <div className="rounded-lg border border-[#FECACA] bg-[#FEE2E2]/40 px-5 py-4" data-testid="declined-banner">
          <p className="flex items-center gap-2 text-sm font-medium text-[#991B1B]"><XCircle className="h-4 w-4" /> You declined this enquiry.</p>
        </div>
      )}

      {inv.status === "participated" && inv.response && (
        <div className="rounded-lg border border-[#BBF7D0] bg-card" data-testid="submitted-offer">
          <h2 className="border-b border-[#BBF7D0] px-5 py-3 font-heading text-base font-semibold text-[#166534]">Your Submitted Offer</h2>
          {inv.response.items && (
            <div className="overflow-x-auto border-b border-[#BBF7D0] px-5 py-3">
              <table className="w-full text-sm" data-testid="submitted-offer-items">
                <thead><tr className="border-b border-[#BBF7D0] text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-1.5 pr-4 font-medium">Material</th><th className="py-1.5 pr-4 font-medium text-right">Unit Price</th><th className="py-1.5 font-medium text-right">Base Value</th>
                </tr></thead>
                <tbody>
                  {inv.response.items.map((oi) => {
                    const enqItem = activeItems.find((x) => x.material_code === oi.material_code) || {};
                    return (
                      <tr key={oi.material_code} className="border-b border-[#BBF7D0]/60 last:border-0">
                        <td className="py-1.5 pr-4 font-mono text-xs">{oi.material_code} <span className="text-muted-foreground">· {enqItem.quantity} {enqItem.uom}</span></td>
                        <td className="py-1.5 pr-4 text-right font-mono text-xs font-semibold">{fmtMoney(oi.unit_price)}</td>
                        <td className="py-1.5 text-right font-mono text-xs">{fmtMoney(oi.line_value ?? (enqItem.quantity ? oi.unit_price * enqItem.quantity : null))}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="grid grid-cols-2 gap-x-6 gap-y-4 px-5 py-4 md:grid-cols-4" data-testid="submitted-offer-summary">
            <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Packaging</p><p className="mt-1 font-mono text-sm">{fmtMoney(inv.response.packaging_cost)}</p></div>
            <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Freight</p><p className="mt-1 font-mono text-sm">{fmtMoney(inv.response.freight_cost)}</p></div>
            <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Total Base Value</p><p className="mt-1 font-mono text-sm font-semibold" data-testid="seller-offer-base">{fmtMoney(inv.response.base_value)}</p></div>
            <div><p className="text-xs uppercase tracking-wide text-muted-foreground">GST ({inv.response.gst_pct}%)</p><p className="mt-1 font-mono text-sm">{fmtMoney(inv.response.gst_amount)}</p></div>
            <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Total Final Value</p><p className="mt-1 font-mono text-sm font-bold text-[#166534]" data-testid="seller-offer-final">{fmtMoney(inv.response.final_value)}</p></div>
            <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Delivery</p><p className="mt-1 font-mono text-sm">{inv.response.delivery_days} days</p></div>
            <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Incoterms</p><p className="mt-1 font-mono text-sm">{inv.response.incoterms}</p></div>
            <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Payment Terms</p><p className="mt-1 text-sm">{inv.response.payment_terms}</p></div>
            <div className="col-span-2 md:col-span-3"><p className="text-xs uppercase tracking-wide text-muted-foreground">Technical Specifications</p><p className="mt-1 text-sm">{inv.response.tech_specs}</p></div>
            {inv.response.comment && <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Comments</p><p className="mt-1 text-sm">{inv.response.comment}</p></div>}
          </div>
          {my_attachments.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 border-t border-[#BBF7D0] px-5 py-3">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">Your Attachments</span>
              {my_attachments.map((f) => (
                <button key={f.id} data-testid={`my-attachment-${f.id.slice(0, 8)}`} onClick={() => downloadFile(`/enquiry-files/${f.id}/download`, f.filename)}
                  className="flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs hover:bg-slate-50">
                  <Paperclip className="h-3 w-3" /> {f.filename}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <SellerAuctionPanel inviteId={inviteId} />

      {canAct && (
        <div className="rounded-lg border bg-card" data-testid="offer-section">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-3">
            <h2 className="font-heading text-base font-semibold">{reviewRequired ? "Review & Resubmit Your Quotation" : "Respond to Enquiry"}</h2>
            <div className="flex gap-2">
              {inv.status !== "intends" && inv.status !== "participated" && (
                <Button variant="outline" size="sm" data-testid="intend-participate-btn" onClick={() => intent("participate")}>
                  <CheckCircle2 className="mr-1.5 h-4 w-4 text-[#166534]" /> Intend to Participate
                </Button>
              )}
              {inv.status !== "participated" && (
                <Button variant="outline" size="sm" data-testid="decline-enquiry-btn" onClick={() => setDeclineOpen(true)} className="border-red-200 text-red-700 hover:bg-red-50">
                  <XCircle className="mr-1.5 h-4 w-4" /> Decline
                </Button>
              )}
            </div>
          </div>
          <form onSubmit={submitOffer} className="space-y-4 px-5 py-4">
            <div>
              <Label>Unit Price per Item (₹) *</Label>
              <div className="mt-1.5 divide-y rounded-md border" data-testid="offer-items-pricing">
                {activeItems.map((it, i) => (
                  <div key={it.id || i} className="flex flex-wrap items-center gap-3 px-3 py-2.5" data-testid={`offer-item-price-${i}`}>
                    <div className="min-w-0 flex-1">
                      <p className="font-mono text-xs font-semibold">{it.material_code}</p>
                      <p className="truncate text-xs text-muted-foreground">{it.description} · {it.quantity} {it.uom}</p>
                    </div>
                    <Input data-testid={`offer-unit-price-${i}`} required type="number" min="0" step="any" className="w-36" placeholder="0.00" value={prices[it.material_code] ?? ""} onChange={(e) => setPrices({ ...prices, [it.material_code]: e.target.value })} />
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <div><Label>GST % *</Label><Input data-testid="offer-gst" required type="number" min="0" max="100" step="any" className="mt-1.5" placeholder="18" value={offer.gst_pct} onChange={(e) => setOffer({ ...offer, gst_pct: e.target.value })} /></div>
              <div><Label>Delivery (days) *</Label><Input data-testid="offer-delivery-days" required type="number" min="1" className="mt-1.5" value={offer.delivery_days} onChange={(e) => setOffer({ ...offer, delivery_days: e.target.value })} /></div>
              <div><Label>Packaging Cost (₹, optional)</Label><Input data-testid="offer-packaging-cost" type="number" min="0" step="any" className="mt-1.5" placeholder="0.00" value={offer.packaging_cost} onChange={(e) => setOffer({ ...offer, packaging_cost: e.target.value })} /></div>
              <div><Label>Freight Cost (₹, optional)</Label><Input data-testid="offer-freight-cost" type="number" min="0" step="any" className="mt-1.5" placeholder="0.00" value={offer.freight_cost} onChange={(e) => setOffer({ ...offer, freight_cost: e.target.value })} /></div>
              <div><Label>Incoterms *</Label>
                <Select value={offer.incoterms} onValueChange={(v) => setOffer({ ...offer, incoterms: v })}>
                  <SelectTrigger data-testid="offer-incoterms" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{INCOTERM_OPTIONS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Payment Terms *</Label><Input data-testid="offer-payment-terms" required className="mt-1.5" placeholder="30 days credit" value={offer.payment_terms} onChange={(e) => setOffer({ ...offer, payment_terms: e.target.value })} /></div>
            </div>
            <div className="rounded-md border bg-slate-50 p-4" data-testid="commercial-summary">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Commercial Summary (preview — final values verified on submission)</p>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <div><p className="text-xs text-muted-foreground">Total Base Value</p><p className="font-mono text-sm font-semibold" data-testid="summary-base-value">{fmtMoney(totalBase)}</p></div>
                <div><p className="text-xs text-muted-foreground">GST Amount</p><p className="font-mono text-sm" data-testid="summary-gst-amount">{fmtMoney(gstAmount)}</p></div>
                <div><p className="text-xs text-muted-foreground">Packaging + Freight</p><p className="font-mono text-sm">{fmtMoney(num(offer.packaging_cost) + num(offer.freight_cost))}</p></div>
                <div><p className="text-xs text-muted-foreground">Total Final Value</p><p className="font-mono text-sm font-bold text-[#166534]" data-testid="summary-final-value">{fmtMoney(finalValue)}</p></div>
              </div>
            </div>
            <div><Label>Technical Specifications *</Label><Textarea data-testid="offer-tech-specs" required rows={3} className="mt-1.5" placeholder="Grade, tolerances, certifications, make/brand…" value={offer.tech_specs} onChange={(e) => setOffer({ ...offer, tech_specs: e.target.value })} /></div>
            <div><Label>Comments (optional)</Label><Textarea data-testid="offer-comment" rows={2} className="mt-1.5" value={offer.comment} onChange={(e) => setOffer({ ...offer, comment: e.target.value })} /></div>
            <div className="flex flex-wrap items-center gap-2">
              <input ref={fileRef} type="file" className="hidden" data-testid="offer-attachment-input" onChange={uploadFile} />
              <Select value={uploadCat} onValueChange={setUploadCat}>
                <SelectTrigger data-testid="offer-attachment-category" className="h-8 w-36 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="technical">Technical</SelectItem>
                  <SelectItem value="commercial">Commercial</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
              <Button type="button" variant="outline" size="sm" data-testid="offer-upload-btn" onClick={() => fileRef.current?.click()}><Paperclip className="mr-1.5 h-3.5 w-3.5" /> Attach File</Button>
              {my_attachments.map((f) => <span key={f.id} className="text-xs text-muted-foreground">{f.filename}</span>)}
              <Button type="submit" data-testid="offer-submit-btn" disabled={saving || needsTermsAcceptance} className="ml-auto bg-slate-900 hover:bg-slate-800">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {reviewRequired ? "Resubmit Offer" : "Submit Offer"}
              </Button>
            </div>
            {needsTermsAcceptance && (
              <p className="rounded-md border border-[#FDE68A] bg-[#FEF3C7]/60 px-3 py-2 text-sm text-[#92400E]" data-testid="terms-block-submit-msg">
                You must accept the Buyer's Terms &amp; Conditions before submitting your response.
              </p>
            )}
            {declineOpen && (
              <div className="flex flex-wrap items-center gap-2 rounded-md border border-[#FECACA] bg-[#FEE2E2]/40 p-3" data-testid="decline-box">
                <Input data-testid="decline-reason-input" className="h-8 max-w-sm bg-white text-sm" placeholder="Reason for declining (optional)" value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} />
                <Button size="sm" data-testid="decline-confirm-btn" onClick={() => intent("decline", declineReason)} className="bg-red-700 hover:bg-red-800">Decline Participation</Button>
                <Button size="sm" variant="ghost" data-testid="decline-cancel-btn" onClick={() => setDeclineOpen(false)}>Cancel</Button>
              </div>
            )}
          </form>
        </div>
      )}
    </div>
  );
}
